import {
  ChevronDownIcon,
  ChevronUpIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { useEffect, useRef } from "react";
import { cn } from "@/core/utils/utils";
import type { PaneSearch } from "./use-pane-search";

const ICON =
  "flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-secondary-text transition-colors hover:bg-surface-secondary hover:text-primary-text focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:text-stroke-secondary";

/**
 * Find in this pane, drawn like the spreadsheet's own find bar.
 *
 * Collapsed it is one icon at the end of the control row; opened it becomes a
 * flat find bar in the same band — icon, field, `N of M`, previous, next,
 * close. It is deliberately not a floating widget: these rows already hold the
 * pane's controls, and a popover over the sheet would cover the thing being
 * searched.
 *
 * Enter and Shift+Enter walk the matches, Escape closes. Those are the keys
 * the grid's search uses and the keys every other find bar uses.
 */
export const PaneSearchControl = ({
  search,
  label,
  className,
}: {
  search: PaneSearch;
  /** Names the field, e.g. "roster". */
  label: string;
  className?: string;
}) => {
  const input = useRef<HTMLInputElement>(null);
  // ⌘F only belongs to a pane that has this bar on screen.
  useEffect(search.attach, [search.attach]);
  useEffect(() => {
    if (!search.open) return;
    input.current?.focus();
    input.current?.select();
  }, [search.focusRequest, search.open]);

  if (!search.open)
    return (
      <button
        type="button"
        aria-label={`Search the ${label}`}
        title={`Search the ${label}`}
        onClick={search.show}
        className={cn(ICON, className)}
      >
        <MagnifyingGlassIcon className="h-4 w-4" />
      </button>
    );

  const hits = search.matches.length;
  const typed = search.query.trim().length > 0;
  return (
    <div className={cn("flex shrink-0 items-center gap-1", className)}>
      <MagnifyingGlassIcon
        aria-hidden
        className="h-4 w-4 shrink-0 text-tertiary-text"
      />
      <input
        ref={input}
        type="text"
        value={search.query}
        onChange={(event) => search.setQuery(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            event.preventDefault();
            search.hide();
            return;
          }
          if (event.key === "Enter" && hits) {
            event.preventDefault();
            search.goTo(event.shiftKey ? -1 : 1);
          }
        }}
        aria-label={`Search the ${label}`}
        placeholder={`Find in ${label}`}
        // No focus ring. The field is focused the moment it opens and stays
        // focused the whole time it is on screen, so a ring would be
        // permanently lit and would say nothing. The caret carries that.
        className="h-7 w-44 bg-transparent px-1 text-sm text-primary-text outline-none placeholder:text-tertiary-text"
      />
      {typed && (
        <span
          className={cn("shrink-0 whitespace-nowrap text-sm", {
            "text-secondary-text": hits > 0,
            "text-negative-text": hits === 0,
          })}
        >
          {hits ? `${search.at + 1} of ${hits}` : "No matches"}
        </span>
      )}
      <button
        type="button"
        aria-label="Previous match"
        disabled={!hits}
        onClick={() => search.goTo(-1)}
        className={ICON}
      >
        <ChevronUpIcon className="h-4 w-4" />
      </button>
      <button
        type="button"
        aria-label="Next match"
        disabled={!hits}
        onClick={() => search.goTo(1)}
        className={ICON}
      >
        <ChevronDownIcon className="h-4 w-4" />
      </button>
      <button
        type="button"
        aria-label="Close search"
        onClick={search.hide}
        className={ICON}
      >
        <XMarkIcon className="h-4 w-4" />
      </button>
    </div>
  );
};
